import { useCallback, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { PosTab } from '../core/pos-shell-page.types';

type UseQuickChargeParams = {
  setActiveTab: Dispatch<SetStateAction<PosTab>>;
  openQuickChargeOrder: () => Promise<number | null>;
  applyAmount: (orderId: number, amount: number) => Promise<void>;
  cancelOrder: (orderId: number) => Promise<void> | void;
  // Domain-owned: the empty QC order id needing the amount modal (fresh open OR empty re-entry).
  amountPromptOrderId: number | null;
  clearAmountPrompt: () => void;
  onError?: (message: string) => void;
};

// Architectural summary: Drives quick-charge; a domain-flagged amount modal charges or cancels.
export function useQuickCharge({
  setActiveTab,
  openQuickChargeOrder,
  applyAmount,
  cancelOrder,
  amountPromptOrderId,
  clearAmountPrompt,
  onError,
}: UseQuickChargeParams) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLockRef = useRef(false);

  const isAmountModalOpen = amountPromptOrderId != null;

  // Opens a fresh QC order; the re-entry effect lands it on payment and flags the amount modal.
  const start = useCallback(async () => {
    try {
      const orderId = await openQuickChargeOrder();
      if (orderId == null) {
        return;
      }
      setActiveTab('orders');
    } catch (error: unknown) {
      onError?.(error instanceof Error ? error.message : 'Failed starting quick charge');
    }
  }, [onError, openQuickChargeOrder, setActiveTab]);

  // Cancel from the amount modal = release the empty QC order and return to the areas view.
  const cancel = useCallback(async () => {
    if (isSubmitting) return;
    const orderId = amountPromptOrderId;
    clearAmountPrompt();
    setActiveTab('areas');
    if (orderId != null) {
      try {
        await cancelOrder(orderId);
      } catch (error: unknown) {
        onError?.(error instanceof Error ? error.message : 'Failed canceling quick charge');
      }
    }
  }, [amountPromptOrderId, cancelOrder, clearAmountPrompt, isSubmitting, onError, setActiveTab]);

  // Confirm the amount; the domain materializes it and keeps us on the payment screen.
  const confirmAmount = useCallback(async (amount: number) => {
    const orderId = amountPromptOrderId;
    if (orderId == null || submitLockRef.current) return;
    submitLockRef.current = true;
    setIsSubmitting(true);
    try {
      await applyAmount(orderId, amount);
      clearAmountPrompt();
    } catch (error: unknown) {
      onError?.(error instanceof Error ? error.message : 'Failed starting quick charge');
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  }, [amountPromptOrderId, applyAmount, clearAmountPrompt, onError]);

  return { isAmountModalOpen, isSubmitting, start, cancel, confirmAmount };
}
